import { useEffect, useMemo, useState } from "react";
import {
  createEmployeeLeaveRequest,
  getEmployeeLeaveOverview,
} from "../api/instateamLeavesApi";
import { AppIcon, StatusBanner } from "../components/InstateamEmployeeComponents";
import {
  buildEmployeeLeavesCalendar,
  downloadLeaveCsv,
  EMPLOYEE_LEAVE_DURATION_OPTIONS,
  formatMonthHeading,
  getCurrentMonthKey,
  getLeaveStatusMeta,
  getLeaveTypeMeta,
  shiftMonthKey,
} from "../utils/employeeLeavesHelpers";

const INITIAL_LEAVE_FORM = {
  leaveType: "casual_leave",
  duration: "full_day",
  fromDateKey: "",
  toDateKey: "",
  reason: "",
};

const EMPTY_EMPLOYEE_LEAVE_METRICS = {
  totalLeaves: 0,
  leavesTaken: 0,
  leavesPending: 0,
  leavesApproved: 0,
  leavesRejected: 0,
};

function SummaryCard({ icon, tone, label, value, helper }) {
  return (
    <article className="employee-leaves-metric-card">
      <div className={`employee-leaves-metric-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{helper}</small>
      </div>
    </article>
  );
}

function LeaveRequestModal({
  form,
  leaveTypes,
  leaveBalanceByType,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}) {
  return (
    <div className="employee-modal-overlay" role="dialog" aria-modal="true">
      <div className="employee-regularization-modal employee-leave-modal">
        <div className="employee-attendance-card-header">
          <div>
            <h3>Apply for Leave</h3>
            <p className="employee-modal-copy">
              Submit your leave request with the required dates and reason.
            </p>
          </div>

          <button className="employee-calendar-arrow" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="employee-leave-request-form" onSubmit={onSubmit}>
          <label className="form-field">
            <span>Leave Type</span>
            <div className="field-shell select-shell">
              <select
                value={form.leaveType}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    leaveType: event.target.value,
                  }))
                }
              >
                {leaveTypes.map((type) => {
                  const balanceEntry = leaveBalanceByType.get(type.key);
                  const isUnavailable =
                    type.dynamicAllocation && Number(balanceEntry?.remainingDays || 0) <= 0;

                  return (
                    <option key={type.key} value={type.key} disabled={isUnavailable}>
                      {type.label}
                      {type.key === "comp_off"
                        ? ` (${balanceEntry?.remainingDays || 0} available)`
                        : ""}
                    </option>
                  );
                })}
              </select>
              <AppIcon name="chevronDown" />
            </div>
          </label>

          <label className="form-field">
            <span>Duration</span>
            <div className="field-shell select-shell">
              <select
                value={form.duration}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    duration: event.target.value,
                    toDateKey:
                      event.target.value === "multiple_days"
                        ? current.toDateKey
                        : current.fromDateKey,
                  }))
                }
              >
                {EMPLOYEE_LEAVE_DURATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </div>
          </label>

          <div className="employee-leave-request-grid">
            <label className="form-field">
              <span>From Date</span>
              <div className="field-shell">
                <input
                  type="date"
                  value={form.fromDateKey}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      fromDateKey: event.target.value,
                      toDateKey:
                        current.duration === "multiple_days"
                          ? current.toDateKey
                          : event.target.value,
                    }))
                  }
                />
                <AppIcon name="calendar" />
              </div>
            </label>

            <label className="form-field">
              <span>To Date</span>
              <div className="field-shell">
                <input
                  type="date"
                  value={form.duration === "multiple_days" ? form.toDateKey : form.fromDateKey}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      toDateKey: event.target.value,
                    }))
                  }
                  disabled={form.duration !== "multiple_days"}
                />
                <AppIcon name="calendar" />
              </div>
            </label>
          </div>

          <label className="form-field">
            <span>Reason</span>
            <div className="employee-textarea-shell">
              <textarea
                value={form.reason}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    reason: event.target.value,
                  }))
                }
                placeholder="Add the reason for this leave request..."
              />
            </div>
          </label>

          <div className="employee-regularization-actions">
            <button className="secondary-button" type="button" onClick={onClose}>
              Cancel
            </button>
            <button className="primary-button" type="submit">
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LeaveInfoModal({ leave, onClose }) {
  if (!leave) return null;

  const statusMeta = getLeaveStatusMeta(leave.status);

  return (
    <div className="employee-modal-overlay" role="dialog" aria-modal="true">
      <div className="employee-regularization-modal employee-leave-modal">
        <div className="employee-attendance-card-header">
          <div>
            <h3>{leave.leaveTypeLabel}</h3>
            <p className="employee-modal-copy">
              Review the full leave request details and status below.
            </p>
          </div>

          <button className="employee-calendar-arrow" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="employee-leave-detail-grid">
          <div className="employee-profile-detail-item">
            <div className="employee-profile-detail-icon">
              <AppIcon name={getLeaveTypeMeta(leave.leaveType).icon} />
            </div>
            <div>
              <span>Leave Type</span>
              <strong>{leave.leaveTypeLabel}</strong>
            </div>
          </div>

          <div className="employee-profile-detail-item">
            <div className="employee-profile-detail-icon">
              <AppIcon name="calendar" />
            </div>
            <div>
              <span>Duration</span>
              <strong>{leave.durationLabel}</strong>
            </div>
          </div>

          <div className="employee-profile-detail-item">
            <div className="employee-profile-detail-icon">
              <AppIcon name="calendarCheck" />
            </div>
            <div>
              <span>Date Range</span>
              <strong>
                {leave.fromDateLabel} to {leave.toDateLabel}
              </strong>
            </div>
          </div>

          <div className="employee-profile-detail-item">
            <div className="employee-profile-detail-icon">
              <AppIcon name="badge" />
            </div>
            <div>
              <span>Status</span>
              <strong className={`employee-status-chip ${statusMeta.tone}`}>
                {statusMeta.label}
              </strong>
            </div>
          </div>
        </div>

        <div className="employee-leave-reason-card">
          <strong>Reason</strong>
          <p>{leave.reason || "No reason was shared for this request."}</p>
        </div>

        {leave.reviewNote ? (
          <div className="employee-leave-reason-card review">
            <strong>Review Note</strong>
            <p>{leave.reviewNote}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PolicyModal({ policy, onClose }) {
  if (!policy) return null;

  return (
    <div className="employee-modal-overlay" role="dialog" aria-modal="true">
      <div className="employee-regularization-modal employee-leave-modal">
        <div className="employee-attendance-card-header">
          <div>
            <h3>Leave Policy</h3>
            <p className="employee-modal-copy">{policy.note}</p>
          </div>

          <button className="employee-calendar-arrow" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="employee-leave-policy-list">
          {policy.leaveTypes?.map((type) => (
            <div key={type.key} className="employee-leave-policy-item">
              <div className={`employee-leave-policy-icon ${type.accent}`}>
                <AppIcon name={getLeaveTypeMeta(type.key).icon} />
              </div>
              <div>
                <strong>{type.label}</strong>
                <span>
                  {type.dynamicAllocation
                    ? `${type.allocation} earned day(s) available`
                    : `${type.allocation} days allocated`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function EmployeeLeavesPage({ token, searchText = "" }) {
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey);
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState("requests");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [leaveForm, setLeaveForm] = useState(INITIAL_LEAVE_FORM);

  useEffect(() => {
    let isActive = true;

    async function fetchOverview() {
      try {
        setIsLoading(true);
        const payload = await getEmployeeLeaveOverview(token, monthKey);

        if (!isActive) return;
        setOverview(payload.overview || null);
      } catch (error) {
        if (!isActive) return;
        setStatusMessage({
          type: "error",
          text: error.message,
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    fetchOverview();

    return () => {
      isActive = false;
    };
  }, [monthKey, token]);

  const leaveTypes = overview?.policy?.leaveTypes || [];
  const metrics = overview?.metrics || EMPTY_EMPLOYEE_LEAVE_METRICS;
  const upcomingLeaves = overview?.upcomingLeaves || [];
  const balanceSummary = useMemo(
    () => overview?.balance?.summary || [],
    [overview?.balance?.summary]
  );
  const leaveBalanceByType = useMemo(
    () => new Map(balanceSummary.map((entry) => [entry.key, entry])),
    [balanceSummary]
  );
  const featuredBalance = useMemo(() => {
    const filtered = balanceSummary.filter((entry) => entry.key !== "other");
    const compOffEntry = filtered.find((entry) => entry.key === "comp_off");
    const regularEntries = filtered.filter((entry) => entry.key !== "comp_off");

    if (compOffEntry && Number(compOffEntry.allocation || 0) > 0) {
      return [compOffEntry, ...regularEntries].slice(0, 4);
    }

    return regularEntries.slice(0, 4);
  }, [balanceSummary]);

  const filteredRequests = useMemo(
    () =>
      (overview?.requests || []).filter((leave) => {
        const matchesStatus = statusFilter === "all" || leave.status === statusFilter;
        const query = String(searchText || "").trim().toLowerCase();
        const matchesSearch =
          !query ||
          [
            leave.leaveTypeLabel,
            leave.leaveType,
            leave.durationLabel,
            leave.status,
            leave.fromDateKey,
            leave.toDateKey,
            leave.reason,
          ]
            .join(" ")
            .toLowerCase()
            .includes(query);
        return matchesStatus && matchesSearch;
      }),
    [overview?.requests, searchText, statusFilter]
  );

  const calendar = useMemo(
    () => buildEmployeeLeavesCalendar(monthKey, overview?.calendar || []),
    [monthKey, overview?.calendar]
  );

  function openLeaveRequestModal() {
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(today.getDate()).padStart(2, "0")}`;

    setLeaveForm({
      ...INITIAL_LEAVE_FORM,
      leaveType:
        leaveTypes.find((type) => {
          const balanceEntry = leaveBalanceByType.get(type.key);
          return !type.dynamicAllocation || Number(balanceEntry?.remainingDays || 0) > 0;
        })?.key || "casual_leave",
      fromDateKey: todayKey,
      toDateKey: todayKey,
    });
    setShowRequestModal(true);
  }

  async function refreshOverview(nextMonthKey = monthKey) {
    const payload = await getEmployeeLeaveOverview(token, nextMonthKey);
    setOverview(payload.overview || null);
  }

  async function handleSubmitLeaveRequest(event) {
    event.preventDefault();

    if (!leaveForm.fromDateKey || !leaveForm.reason.trim()) {
      setStatusMessage({
        type: "warning",
        text: "Select the leave dates and enter the reason first.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setStatusMessage(null);

      await createEmployeeLeaveRequest(token, {
        leaveType: leaveForm.leaveType,
        duration: leaveForm.duration,
        fromDateKey: leaveForm.fromDateKey,
        toDateKey:
          leaveForm.duration === "multiple_days"
            ? leaveForm.toDateKey
            : leaveForm.fromDateKey,
        reason: leaveForm.reason,
      });

      setShowRequestModal(false);
      await refreshOverview();
      setStatusMessage({
        type: "success",
        text: "Leave request submitted successfully.",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDownloadReport() {
    const rows = [
      ["Leave Type", "Duration", "From", "To", "Total Days", "Status", "Applied On"],
      ...filteredRequests.map((leave) => [
        leave.leaveTypeLabel,
        leave.durationLabel,
        leave.fromDateKey || leave.fromDateLabel,
        leave.toDateKey || leave.toDateLabel,
        leave.totalDays,
        getLeaveStatusMeta(leave.status).label,
        String(leave.appliedOn || "").slice(0, 10) || leave.appliedOnLabel,
      ]),
    ];

    downloadLeaveCsv(`instateam-leaves-${monthKey}.csv`, rows);
  }

  return (
    <section className="employee-portal-page employee-leaves-page">
      <div className="employee-page-heading employee-attendance-heading">
        <div>
          <h1>Leaves</h1>
          <div className="employee-breadcrumb">
            <span>Dashboard</span>
            <span>></span>
            <span>Leaves</span>
          </div>
        </div>

        <div className="employee-attendance-page-actions">
          <button
            className="secondary-button employee-inline-button"
            type="button"
            onClick={handleDownloadReport}
          >
            <AppIcon name="download" />
            Leave Report
          </button>

          <button
            className="primary-button employee-inline-button"
            type="button"
            onClick={openLeaveRequestModal}
          >
            <AppIcon name="plus" />
            Apply for Leave
          </button>
        </div>
      </div>

      {statusMessage ? (
        <StatusBanner
          type={statusMessage.type}
          text={statusMessage.text}
          onClose={() => setStatusMessage(null)}
        />
      ) : null}

      <div className="employee-leaves-metrics-grid">
        <SummaryCard
          icon="calendarCheck"
          tone="success"
          label="Total Leaves"
          value={metrics.totalLeaves}
          helper="Days"
        />
        <SummaryCard
          icon="checkCircle"
          tone="success"
          label="Leaves Taken"
          value={metrics.leavesTaken}
          helper="Days"
        />
        <SummaryCard
          icon="clock"
          tone="info"
          label="Leaves Pending"
          value={metrics.leavesPending}
          helper="Days"
        />
        <SummaryCard
          icon="calendar"
          tone="warning"
          label="Leaves Approved"
          value={metrics.leavesApproved}
          helper="Days"
        />
        <SummaryCard
          icon="calendarX"
          tone="danger"
          label="Leaves Rejected"
          value={metrics.leavesRejected}
          helper="Days"
        />
      </div>

      <div className="employee-leaves-layout">
        <div className="employee-leaves-main">
          <article className="employee-attendance-card employee-leaves-main-card">
            <div className="employee-leaves-tabs">
              <button
                type="button"
                className={activeTab === "requests" ? "active" : ""}
                onClick={() => setActiveTab("requests")}
              >
                My Leave Requests
              </button>
              <button
                type="button"
                className={activeTab === "balance" ? "active" : ""}
                onClick={() => setActiveTab("balance")}
              >
                Leave Balance
              </button>
              <button
                type="button"
                className={activeTab === "calendar" ? "active" : ""}
                onClick={() => setActiveTab("calendar")}
              >
                Leave Calendar
              </button>
            </div>

            {activeTab === "requests" ? (
              <>
                <div className="employee-leaves-toolbar">
                  <div className="field-shell select-shell employee-leaves-filter-shell">
                    <select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <AppIcon name="chevronDown" />
                  </div>

                  <div className="employee-leaves-range-chip">
                    <AppIcon name="calendar" />
                    <span>{formatMonthHeading(monthKey)}</span>
                  </div>
                </div>

                {isLoading ? (
                  <div className="employee-attendance-loading">
                    Loading leave requests...
                  </div>
                ) : filteredRequests.length ? (
                  <div className="employee-leaves-table-shell">
                    <div className="employee-leaves-table-header">
                      <span>Leave Type</span>
                      <span>Duration</span>
                      <span>From</span>
                      <span>To</span>
                      <span>Total Days</span>
                      <span>Status</span>
                      <span>Applied On</span>
                      <span>Action</span>
                    </div>

                    {filteredRequests.map((leave) => {
                      const statusMeta = getLeaveStatusMeta(leave.status);
                      const typeMeta = getLeaveTypeMeta(leave.leaveType);

                      return (
                        <div key={leave._id} className="employee-leaves-table-row">
                          <span className="employee-leave-type-cell">
                            <span className={`employee-leave-type-icon ${typeMeta.accent}`}>
                              <AppIcon name={typeMeta.icon} />
                            </span>
                            <span>{leave.leaveTypeLabel}</span>
                          </span>
                          <span>{leave.durationLabel}</span>
                          <span>{leave.fromDateDayLabel}</span>
                          <span>{leave.toDateDayLabel}</span>
                          <span>{leave.totalDays} Day</span>
                          <span className={`employee-status-chip ${statusMeta.tone}`}>
                            {statusMeta.label}
                          </span>
                          <span>{leave.appliedOnLabel}</span>
                          <button
                            className="employee-leave-row-action"
                            type="button"
                            onClick={() => setSelectedLeave(leave)}
                          >
                            <AppIcon name="more" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="employee-attendance-empty">
                    No leave requests found for this month.
                  </div>
                )}
              </>
            ) : null}

            {activeTab === "balance" ? (
              <div className="employee-leave-balance-grid">
                {balanceSummary.map((balance) => {
                  const typeMeta = getLeaveTypeMeta(balance.key);

                  return (
                    <article key={balance.key} className="employee-leave-balance-card">
                      <div className={`employee-leave-balance-icon ${typeMeta.accent}`}>
                        <AppIcon name={typeMeta.icon} />
                      </div>
                      <strong>{balance.label}</strong>
                      <span>
                        {balance.remainingDays} / {balance.allocation} Days
                      </span>
                      <small>{balance.usedDays} day(s) used</small>
                    </article>
                  );
                })}
              </div>
            ) : null}

            {activeTab === "calendar" ? (
              <>
                <div className="employee-attendance-card-header">
                  <h3>Leave Calendar</h3>
                  <div className="employee-attendance-calendar-tools">
                    <button
                      className="employee-calendar-arrow"
                      type="button"
                      onClick={() => setMonthKey((current) => shiftMonthKey(current, -1))}
                    >
                      <AppIcon name="chevronLeft" />
                    </button>
                    <strong>{formatMonthHeading(monthKey)}</strong>
                    <button
                      className="employee-calendar-arrow"
                      type="button"
                      onClick={() => setMonthKey((current) => shiftMonthKey(current, 1))}
                    >
                      <AppIcon name="chevronRight" />
                    </button>
                  </div>
                </div>

                <div className="employee-attendance-legend employee-leaves-legend">
                  <span className="employee-attendance-legend-item">
                    <span className="attendance-dot success" />
                    Approved
                  </span>
                  <span className="employee-attendance-legend-item">
                    <span className="attendance-dot warning" />
                    Pending
                  </span>
                  <span className="employee-attendance-legend-item">
                    <span className="attendance-dot danger" />
                    Rejected
                  </span>
                </div>

                <div className="employee-leaves-calendar-grid">
                  {calendar.weekdays.map((weekday) => (
                    <span key={weekday} className="employee-attendance-weekday">
                      {weekday}
                    </span>
                  ))}

                  {calendar.cells.map((cell) =>
                    cell.isPlaceholder ? (
                      <div key={cell.key} className="employee-leaves-calendar-cell placeholder" />
                    ) : (
                      <div key={cell.key} className="employee-leaves-calendar-cell">
                        <strong>{cell.day}</strong>
                        {cell.status ? (
                          <span className={`employee-status-chip ${cell.meta.tone}`}>
                            {cell.meta.label}
                          </span>
                        ) : null}
                        {cell.leaveType ? (
                          <small>{cell.typeMeta.label}</small>
                        ) : (
                          <small>No request</small>
                        )}
                      </div>
                    )
                  )}
                </div>
              </>
            ) : null}
          </article>
        </div>

        <div className="employee-leaves-side">
          <article className="employee-attendance-card employee-leave-balance-summary-card">
            <div className="employee-attendance-card-header">
              <div>
                <h4>Leave Balance</h4>
                <p>As of today</p>
              </div>
            </div>

            <div className="employee-leave-balance-summary-list">
              {featuredBalance.map((balance) => {
                const typeMeta = getLeaveTypeMeta(balance.key);

                return (
                  <div key={balance.key} className="employee-leave-balance-summary-item">
                    <span className={`employee-leave-type-icon ${typeMeta.accent}`}>
                      <AppIcon name={typeMeta.icon} />
                    </span>
                    <div>
                      <strong>{balance.label}</strong>
                      <small>
                        {balance.remainingDays} / {balance.allocation} Days
                      </small>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              className="secondary-button full-width"
              type="button"
              onClick={() => setActiveTab("balance")}
            >
              View All
            </button>
          </article>

          <article className="employee-attendance-card employee-leave-upcoming-card">
            <div className="employee-attendance-card-header">
              <div>
                <h4>Upcoming Leaves</h4>
                <p>Pending and approved requests</p>
              </div>
            </div>

            <div className="employee-leave-upcoming-list">
              {upcomingLeaves.length ? (
                upcomingLeaves.map((leave) => {
                  const statusMeta = getLeaveStatusMeta(leave.status);
                  const typeMeta = getLeaveTypeMeta(leave.leaveType);

                  return (
                    <div key={leave._id} className="employee-leave-upcoming-item">
                      <span className={`employee-leave-type-icon ${typeMeta.accent}`}>
                        <AppIcon name={typeMeta.icon} />
                      </span>
                      <div>
                        <strong>{leave.leaveTypeLabel}</strong>
                        <span>
                          {leave.totalDays} Day
                          {Number(leave.totalDays) === 1 ? "" : "s"}
                        </span>
                        <small>
                          {leave.fromDateDayLabel}
                          {leave.fromDateKey !== leave.toDateKey
                            ? ` - ${leave.toDateDayLabel}`
                            : ""}
                        </small>
                      </div>
                      <span className={`employee-status-chip ${statusMeta.tone}`}>
                        {statusMeta.label}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="employee-attendance-empty">
                  No upcoming leave requests right now.
                </div>
              )}
            </div>

            <button
              className="secondary-button full-width"
              type="button"
              onClick={() => setActiveTab("calendar")}
            >
              View Calendar
            </button>
          </article>
        </div>
      </div>

      <article className="employee-leave-policy-banner">
        <div className="employee-leave-policy-banner-icon">
          <AppIcon name="alertCircle" />
        </div>
        <div>
          <strong>Leave Policy</strong>
          <p>{overview?.policy?.note}</p>
        </div>
        <button className="secondary-button" type="button" onClick={() => setShowPolicyModal(true)}>
          View Policy
        </button>
      </article>

      {showRequestModal ? (
        <LeaveRequestModal
          form={leaveForm}
          leaveTypes={leaveTypes}
          leaveBalanceByType={leaveBalanceByType}
          onChange={setLeaveForm}
          onClose={() => setShowRequestModal(false)}
          onSubmit={handleSubmitLeaveRequest}
          isSubmitting={isSubmitting}
        />
      ) : null}

      {selectedLeave ? (
        <LeaveInfoModal leave={selectedLeave} onClose={() => setSelectedLeave(null)} />
      ) : null}

      {showPolicyModal ? (
        <PolicyModal policy={overview?.policy} onClose={() => setShowPolicyModal(false)} />
      ) : null}
    </section>
  );
}
